'use client';

import {
    DirectionEnum,
    FlexBox,
    GapSize,
    Title,
    TitleLevels,
    TitleMarkup,
    TypographyAlign,
} from '@bytel/trilogy-react';

import { FaiTabsEnum, TabConfig } from '@app-types/home-fai';
import { FaiType, OffersByCategoryCode } from '@app-types/plan';

import { Breadcrumb as BreadcrumbComponent } from '@components/breadcrumb';
import { FaiOffersByTabs } from '@components/fai-offers-by-tabs';
import { FaiUpsellModalContainer } from '@components/fai-upsell-modal';
import { Faq } from '@components/faq';
import { Legals } from '@components/legals';
import { SectionContainer } from '@components/section-container';
import { SSIBlock } from '@components/ssi-block';
import { TriggerLogin } from '@components/trigger-login';

import { partials } from '@helpers/partials';

import { PlanTypeId } from '@constants/plan';

import { useActiveTab, useAddToCart, useEligibilityContext, useUpsellModal } from '@home-fai/hooks';

import { ProviderHomeTabConfig, getProviderHomeTabConfig } from '../config/provider-home.config';
import { StickyBar } from './sticky-bar';

interface ProviderHomeProps {
    defaultPlans: FaiType[];
    defaultTab?: FaiTabsEnum;
    tabConfig: TabConfig;
    offersByCategory: OffersByCategoryCode;
}

export function ProviderHome({ defaultPlans, defaultTab, tabConfig, offersByCategory }: Readonly<ProviderHomeProps>) {
    const categoriesToShow: PlanTypeId[] = [PlanTypeId.FAI_HOME];
    const [activeTab, setActiveTab] = useActiveTab({ defaultTab, tabConfig });
    const { plans, idPanierCorbis, homeZone } = useEligibilityContext({ defaultPlans });
    const { upsellModal, showUpsellModal } = useUpsellModal({ defaultPlans });
    const { onAddToCart } = useAddToCart({ plans, idPanierCorbis, homeZone, showUpsellModal });
    const tabUiConfig: ProviderHomeTabConfig = getProviderHomeTabConfig(activeTab);

    return (
        <>
            <SectionContainer>
                <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.SIX}>
                    <Title markup={TitleMarkup.H1} level={TitleLevels.ONE} typo={TypographyAlign.TEXT_CENTERED}>
                        Nos box internet
                    </Title>
                    <SSIBlock url={partials.MARKETING.FAI_HOME_SLIDER} />
                </FlexBox>
            </SectionContainer>
            <SectionContainer>
                <FaiOffersByTabs
                    setActiveTab={setActiveTab}
                    defaultTab={activeTab}
                    tabConfig={tabConfig}
                    offersByCategory={offersByCategory}
                    categoriesToShow={categoriesToShow}
                    plans={plans}
                    onAddToCart={onAddToCart}
                    showComparatorSelection
                />
            </SectionContainer>
            {tabUiConfig.ssiBlocks?.map(({ url }) => (
                <SSIBlock key={url} url={url} />
            ))}
            <SectionContainer>
                <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.FIVE}>
                    <Faq
                        title={tabUiConfig.faq.title}
                        titleAlt={tabUiConfig.faq.titleAlt}
                        url={tabUiConfig.faq.url}
                        className={tabUiConfig.faq.wrapper?.rowClassName}
                    />
                    <BreadcrumbComponent
                        elements={[
                            {
                                name: 'home',
                                label: 'Accueil',
                                href: '/',
                            },
                            {
                                name: 'home FAI',
                                label: 'Offres internet',
                            },
                        ]}
                    />
                    {tabUiConfig.legals && (
                        <Legals key={tabUiConfig.legals.id} id={tabUiConfig.legals.id} url={tabUiConfig.legals.url} />
                    )}
                </FlexBox>
            </SectionContainer>
            <StickyBar activeTab={activeTab} />
            <TriggerLogin />
            {upsellModal && <FaiUpsellModalContainer {...upsellModal} />}
        </>
    );
}
